import { useState } from 'react';
import { formatDateKey, parseDateKey } from '../components/calendar/calendar-utils';

interface DateDraft {
  date: Date | null;
  syncedIso: string;
}

export interface DraftDateOnlyValue {
  /** Da passare al `value` del DatePicker: riferimento stabile, anche invalido mentre si digita. */
  pickerValue: Date | null;
  /** Da passare all'`onChange` del DatePicker. */
  handlePickerChange: (date: Date | null) => void;
}

function isUsableDate(date: Date | null): date is Date {
  return date !== null && !Number.isNaN(date.getTime());
}

/**
 * MUI X confronta per riferimento il `value` con l'ultimo ricevuto e, se cambia, riscrive le
 * sezioni digitate. Un `value` derivato dalla stringa ISO del form (`undefined` finche' la data
 * e' parziale, nuovo `Date` a ogni render) azzera giorno e mese mentre l'utente scrive l'anno.
 * Il picker riceve quindi la bozza locale; il form riceve `''` finche' la data non e' valida e si
 * riallinea alla bozza solo quando `isoValue` cambia dall'esterno (reset, suggerimento).
 */
export function useDraftDateOnlyValue(
  isoValue: string,
  onIsoChange: (isoValue: string) => void,
): DraftDateOnlyValue {
  const [draft, setDraft] = useState<DateDraft>(() => ({
    date: parseDateKey(isoValue),
    syncedIso: isoValue,
  }));

  if (draft.syncedIso !== isoValue) {
    setDraft({ date: parseDateKey(isoValue), syncedIso: isoValue });
  }

  function handlePickerChange(date: Date | null) {
    const nextIso = isUsableDate(date) ? formatDateKey(date) : '';
    setDraft({ date, syncedIso: nextIso });
    onIsoChange(nextIso);
  }

  return { pickerValue: draft.date, handlePickerChange };
}
